import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Tweens } from "../Components/Effects/Tweens";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { Payline } from "../GameComponents/Payline";
import { Paylines } from "../GameComponents/Paylines";
import { Symbol } from "../GameComponents/Symbol";
import { Symbols } from "../GameComponents/Symbols";
import { ImageTextCounterElement2 } from "./ImageTextCounterElement2";
import { DataManager } from "../Managers/DataManager";
import { Languages } from "../Utils/Languages";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimator2 extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly StartFreeSpins: string = "startFreespins";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    private static readonly ShowFirstWinsExpand: string = "showFirstWinsExpand";

    private static readonly ShowFirstWinsStep1: string = "showFirstWinsStep1";

    private static readonly ShowFirstWinsStep2: string = "showFirstWinsStep2";

    private static readonly ShowFirstWinsStep3: string = "showFirstWinsStep3";

    private static readonly ShowFirstWinsStep4: string = "showFirstWinsStep4";

    private static readonly ShowFirstWinsStep5: string = "showFirstWinsStep5";

    private static readonly IdleStep: string = "idleStep";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private inFreeSpins: boolean;

    private currentWins2: Array<Communication.Game.IWin>;
    private currentWinsHelp: number;

    private counter: ImageTextCounterElement2;

    private currentCounterStep: number;
    private currentExpandStep: number;
    private currentIdleSteps: Array<Array<Array<number>>>;

    private paths: Array<{
        parts: Array<ImageElement>;
        borders: Array<ImageElement>;
    }>;

    private teasing: SwitchElement;
    private pharaohs: SwitchElement;

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        inFreeSpins?: boolean,
        teasing?: SwitchElement,
        pharaohs?: SwitchElement,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this.inFreeSpins = inFreeSpins;
        this.teasing = teasing;
        this.pharaohs = pharaohs;
    }

    //******************************************************************************************************************
    //  ON REEL STOPPED, SHOW SYMBOLS
    //******************************************************************************************************************

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        this.tease(reelIndex);
    }

    private tease(reelIndex: number): void {
        const count = this.countScatters(reelIndex);

        if (count >= this.getMinTeasingSymbolCount()) {
            this.tease3(reelIndex, count);
        } else if (count === this.getMinTeasingSymbolCount() - 1) {
            const helpCount = reelIndex > 0 ? this.countScatters(reelIndex - 1) : 0;

            if (helpCount === count) {
                this.tease3(reelIndex, count);
            } else {
                this.tease2(reelIndex, count);
            }
        } else {
            this.tease1(reelIndex, count);
        }
    }

    private countScatters(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            if (this.currentReels[i].symbols.indexOf(this.getTeasingSymbol()) !== -1) {
                count++;
            }
        }

        return count;
    }

    private tease1(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            return;
        }

        const symbolIndex = this.currentReels[reelIndex].symbols.indexOf(this.getTeasingSymbol());

        if (symbolIndex != -1) {
            if (this.allowSymbolGoToCase(reelIndex, symbolIndex, Symbol.CaseImpact)) {
                this.currentSymbols[reelIndex][symbolIndex].goToCase(Symbol.CaseImpact);
            }
        }
    }

    private tease2(reelIndex: number, count: number): void {
        if (this.reelCount - reelIndex - 1 < this.getMinTeasingSymbolCount() - count) {
            for (let i = 0; i < reelIndex; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNormal)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNormal);
                    }
                }
            }

            return;
        }

        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j].id === this.getTeasingSymbol() - 1) {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseTease)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseTease);
                    }
                } else {
                    if (this.allowSymbolGoToCase(i, j, Symbol.CaseNowin)) {
                        this.currentSymbols[i][j].goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        this.tease3(reelIndex, count);
    }

    private tease3(reelIndex: number, count: number): void {
        for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
            if (this.currentSymbols[reelIndex][i].id === this.getTeasingSymbol() - 1) {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseImpactTease)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseImpactTease);
                }
            } else {
                if (this.allowSymbolGoToCase(reelIndex, i, Symbol.CaseNowin)) {
                    this.currentSymbols[reelIndex][i].goToCase(Symbol.CaseNowin);
                }
            }
        }
    }

    private getTeasingSymbol(): number {
        return 10;
    }

    private getMinTeasingSymbolCount(): number {
        return 3;
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        if (wins != null && wins.length > 1) {
            const parsedWins: Array<Communication.Game.IWin> = [];

            let scatterWin: Communication.Game.IWin = null;
            for (let i = 0; i < wins.length; i++) {
                if (wins[i].type !== Communication.Game.WinType.freeSpins) {
                    if (wins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                        scatterWin = wins[i];
                    } else {
                        parsedWins.push(wins[i]);
                    }
                }
            }

            if (scatterWin != null) {
                parsedWins.push(scatterWin);
            }

            wins = parsedWins;
        }

        this.allSymbolsGoToCase(Symbol.CaseNormal, false);

        this.resetCounter();

        return super.showWins(wins, win);
    }

    protected showFirstWins(): void {
        this.currentWins2 = null;
        this.currentWinsHelp = 0;

        if (this.inFreeSpins) {
            this.currentMultiplierIndex = Math.min(this.currentMultiplierIndex, 2);
        }

        if (this.currentMultiplierIndex >= 3) {
            // PaytableClient.sendGameWin(this.currentWins);

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.allPaylinesGoToCase(Payline.CaseNowin);

            let isWild =
                this.currentWins[this.currentWins.length - 1].trigger === Communication.Game.WinTrigger.Scatter;

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseLive, false);
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                this.winPaylineGoToCase(i, Payline.CaseWin);

                if (!isWild) {
                    const win = this.currentWins[i];

                    for (let j = 0; j < win.symbols.length; j++) {
                        if (win.symbols[j] === 10) {
                            isWild = true;

                            PlayList.play("SND_SymBookSmall");
                            break;
                        }
                    }
                }
            }

            this.startAnimation();
        } else {
            if (this.inFreeSpins) {
                if (this.pharaohs != null) {
                    this.pharaohs.goToCase("SHOW");
                }

                const wins1: Array<Communication.Game.IWin> = [];
                const wins2: Array<Communication.Game.IWin> = [];

                for (let i = 0; i < this.currentWins.length; i++) {
                    if (this.currentWins[i].customData != null && this.currentWins[i].customData["expanded"]) {
                        wins2.push(this.currentWins[i]);
                    } else {
                        wins1.push(this.currentWins[i]);
                    }
                }

                if (wins2.length > 0) {
                    this.currentWins = wins1;
                    this.currentWins2 = wins2;

                    if (wins1.length > 0) {
                        this.currentWinsHelp = wins1.length;

                        this.showFirstWinsStep1(false);
                    } else {
                        this.showFirstWinsExpand1();
                    }
                } else {
                    this.showFirstWinsStep1(false);
                }
            } else {
                this.showFirstWinsStep1(false);
            }
        }
    }

    private showFirstWinsExpand1(): void {
        this.allPaylinesGoToCase(Payline.CaseNowin);

        this.currentExpandStep = 0;

        this.showFirstWinsExpand2();
    }

    private showFirstWinsExpand2(): void {
        if (this.currentReels[this.currentExpandStep].transform != null) {
            PlayList.play("SND_FSSymbolHit" + this.currentExpandStep);

            for (let i = 0; i < 3; i++) {
                this.removeCurrentSymbol(this.currentExpandStep, i);
                this.addCurrentSymbol(
                    this.currentExpandStep,
                    i,
                    this.currentReels[this.currentExpandStep].transform[i] + 10,
                );

                if (this.allowSymbolGoToCase(this.currentExpandStep, i, Symbol.CaseExpand)) {
                    this.currentSymbols[this.currentExpandStep][i].goToCase(Symbol.CaseExpand);
                }
            }
        } else {
            PlayList.play("SND_FSSymbolNoHit" + this.currentExpandStep);

            for (let i = 0; i < 3; i++) {
                if (this.allowSymbolGoToCase(this.currentExpandStep, i, Symbol.CaseExpand)) {
                    this.currentSymbols[this.currentExpandStep][i].goToCase(Symbol.CaseExpand);
                }
            }
        }

        this.currentExpandStep++;

        this.startTimer(
            this.currentExpandStep < 5 ? WinAnimator2.ShowFirstWinsExpand : WinAnimator2.ShowFirstWinsStep1,
            20,
        );
    }

    private showFirstWinsStep1(join: boolean): void {
        if (join) {
            // PaytableClient.resetPaytable();
            // PaytableClient.sendGameWin(this.currentWins2);

            if (this.currentWins.length === 0) {
                this.currentCounterStep = 0;
            }

            this.currentWins = this.currentWins.concat(this.currentWins2);
            this.currentWins2 = [];

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);

            for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseLive, false);
            }

            this.showFirstWinsStep2();
        } else {
            // PaytableClient.resetPaytable();
            // PaytableClient.sendGameWin(this.currentWins);

            this.currentCounterStep = 0;

            this.allSymbolsGoToCase(Symbol.CaseNowin, true);

            for (let i = 0; i < this.currentWins.length; i++) {
                this.winSymbolsGoToCase(i, Symbol.CaseLive, false);
            }

            this.showFirstWinsStep2();
        }
    }

    private showFirstWinsStep2(): void {
        const win = this.currentWins[this.currentCounterStep];

        if (win.trigger === Communication.Game.WinTrigger.Scatter) {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_FSLoop");
            PlayList.play("SND_SymBook");
        } else {
            for (let i = 0; i < win.symbols.length; i++) {
                if (win.symbols[i] === 10) {
                    PlayList.play("SND_SymBookSmall");
                    break;
                }
            }
        }

        const expanded = this.currentWins2 != null && this.currentWins2.length === 0;

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = expanded ? this.currentWinsHelp : 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        if (expanded) {
            this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseExpandedWin);
            this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseExpandedWin, false);
        } else {
            this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
            this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);
        }

        this.runCounterStep1(expanded);

        this.currentCounterStep++;

        this.startTimer(
            this.currentCounterStep < this.currentWins.length
                ? WinAnimator2.ShowFirstWinsStep2
                : WinAnimator2.ShowFirstWinsStep3,
            expanded ? 20 : 60,
        );
    }

    private showFirstWinsStep3(): void {
        if (this.currentWins2 != null && this.currentWins2.length > 0) {
            this.showFirstWinsExpand1();
        } else {
            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
                this.winPaylineGoToCase(i, Payline.CaseNormal);
            }

            if (this.currentWins[this.currentWins.length - 1].trigger !== Communication.Game.WinTrigger.Scatter) {
                let isWild = false;

                for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
                    this.winSymbolsGoToCase(i, Symbol.CaseWin, false);

                    /*if (!isWild) {
                        const win = this.currentWins[i];

                        for (let j = 0; j < win.symbols.length; j++) {
                            if (win.symbols[j] === 10) {
                                isWild = true;

                                PlayList.play('SND_SymBookSmall');
                                break;
                            }
                        }
                    }*/
                }
            }

            this.runCounterStep2();
            const delay = this.currentMultiplierIndex == 0 ? 45 : 90;

            this.startTimer(WinAnimator2.ShowFirstWinsStep4, delay);
        }
    }

    private showFirstWinsStep4(): void {
        this.currentCounterStep = null;
        this.currentExpandStep = null;

        this.runCounterStep3();

        let scatter = false;

        for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                scatter = true;
                break;
            }
        }

        if (this.inFreeSpins) {
            this.startTimer(WinAnimator2.ShowFirstWinsStep5, scatter ? 45 : 30);
        } else {
            this.startTimer(WinAnimator2.ShowFirstWinsStep5, scatter ? 90 : 30);
        }

        this.emit(WinAnimator.FinishWinCalculation);
    }

    private showFirstWinsStep5(): void {
        this.emit(WinAnimator.FinishWinAnimator);

        this.currentCycle = 0;

        this.cycleWins();
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        if (wins != null && wins.length > 1) {
            const parsedWins: Array<Communication.Game.IWin> = [];

            for (let i = 0; i < wins.length; i++) {
                if (wins[i].type !== Communication.Game.WinType.freeSpins) {
                    parsedWins.push(wins[i]);
                }
            }

            wins = parsedWins;
        }

        super.showInitWins(wins, win);
    }

    protected showFirstInitWins(): void {
        for (let i = 0; i < this.currentReels.length; i++) {
            if (this.currentReels[i].transform != null) {
                for (let j = 0; j < this.currentReels[i].transform.length; j++) {
                    this.removeCurrentSymbol(i, j);
                    this.addCurrentSymbol(i, j, this.currentReels[i].transform[j] + 10);
                }
            }
        }

        this.currentWinsHelp = 0;

        if (this.inFreeSpins) {
            if (this.pharaohs != null) {
                this.pharaohs.goToCase("SHOW");
            }

            const wins2: Array<Communication.Game.IWin> = [];

            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].customData != null && this.currentWins[i].customData["expanded"]) {
                    wins2.push(this.currentWins[i]);
                }
            }

            if (wins2.length > 0) {
                // PaytableClient.sendGameWin(wins2);

                this.currentWinsHelp = this.currentWins.length - wins2.length;
            } else {
                // PaytableClient.sendGameWin(this.currentWins);
            }
        } else {
            // PaytableClient.sendGameWin(this.currentWins);
        }

        this.allSymbolsGoToCase(Symbol.CaseNowin, true);
        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin, false);

            if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                if (this.inFreeSpins) {
                    this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                } else {
                    this.winSymbolsGoToCase(i, Symbol.CaseSecretWin, false);
                }
            } else {
                this.winSymbolsGoToCase(i, Symbol.CaseLive, false);
            }
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger === Communication.Game.WinTrigger.Scatter) {
                return;
            }
        }

        if (this.currentCycle < this.currentWinsHelp) {
            this.currentCycle = this.currentWinsHelp;
        }

        PlayList.play("SND_BaseRecapWinline");

        const win = this.currentWins[this.currentCycle];

        for (let i = 0; i < win.symbols.length; i++) {
            if (win.symbols[i] === 10) {
                PlayList.play("SND_SymBookSmall");
                break;
            }
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = this.currentWinsHelp; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
        }

        this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
        this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean): boolean {
        if (this.inFreeSpins && this.pharaohs != null && this.pharaohs.getCurrentCase() === "SHOW") {
            this.pharaohs.goToCase("HIDE");
        }

        if (this.currentCounterStep != null || this.currentExpandStep != null) {
            this.currentCounterStep = null;
            this.currentExpandStep = null;

            const expanded = this.currentWins2 != null;

            this.runCounterOutro(expanded);

            if (expanded) {
                this._reset(showLastSymbols);

                return true;
            }
        }

        return this._reset(showLastSymbols);
    }

    protected _reset(showLastSymbols: boolean): boolean {
        if (showLastSymbols) {
            if (this.timerStatus !== WinAnimator.Idle && this.timerStatus !== WinAnimator2.IdleStep) {
                this.stopTimer();
            }
        } else {
            this.stopTimer();
        }

        if (showLastSymbols) {
            this.allSymbolsGoToReset();
        } else {
            this.allSymbolsGoToCase(Symbol.CaseEmpty, false);
        }

        if (showLastSymbols && this.timerStatus !== WinAnimator.Idle && this.timerStatus !== WinAnimator2.IdleStep) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        /* Hot fix - play idle animation after SPB outro if timer for idle animation gets interrupted
         * */

        if (
            showLastSymbols &&
            (this.timerStatus == WinAnimator.Idle || this.timerStatus == WinAnimator2.IdleStep) &&
            !this.timer.isPlaying()
        ) {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }

        this.allPaylinesGoToCase(Payline.CaseNormal);

        this.value.visible = false;

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;
                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;
                return lastMultiplierIndex >= 3;
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;
            return lastMultiplierIndex >= 3;
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimator.Idle:
                this.idle();
                break;
            case WinAnimator.CycleWins:
                this.cycleWins();
                break;
            case WinAnimator2.ShowFirstWinsExpand:
                this.showFirstWinsExpand2();
                break;
            case WinAnimator2.ShowFirstWinsStep1:
                this.showFirstWinsStep1(true);
                break;
            case WinAnimator2.ShowFirstWinsStep2:
                this.showFirstWinsStep2();
                break;
            case WinAnimator2.ShowFirstWinsStep3:
                this.showFirstWinsStep3();
                break;
            case WinAnimator2.ShowFirstWinsStep4:
                this.showFirstWinsStep4();
                break;
            case WinAnimator2.ShowFirstWinsStep5:
                this.showFirstWinsStep5();
                break;
            case WinAnimator2.IdleStep:
                this.idleStep();
                break;
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number, customId?: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            let id = this.currentReels[reelIndex].symbols[symbolIndex];

            if (customId) {
                id = customId;
            } else {
                if (this.inFreeSpins && DataManager.SpecialSymbols[0] === id) {
                    id += 10;
                }
            }

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            this.symbolsContainer.addChild(symbol);

            this.currentSymbols[reelIndex][symbolIndex] = symbol;
        }
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        switch (caseType) {
            case Symbol.CaseLive:
                caseType = Symbol.CaseSecretLive;
                break;
            case Symbol.CaseWin:
                if (this.inFreeSpins) {
                    caseType = Symbol.CaseWin;
                } else {
                    caseType = Symbol.CaseSecretWin;
                }
                break;
        }

        return super.winSymbolsGoToCaseAsScatter(winIndex, caseType);
    }

    protected allowSymbolGoToCase(reelIndex: number, symbolIndex: number, caseType: string): boolean {
        if (caseType === Symbol.CaseNowin || caseType === Symbol.CaseNowin + "_NOWIN") {
            const symbol = this.currentSymbols[reelIndex][symbolIndex];

            if (symbol.getCurrentCase() === Symbol.CaseExpand) {
                return false;
            }
        }

        return true;
    }

    //******************************************************************************************************************
    //  PAYLINES
    //******************************************************************************************************************

    protected createPaylines(paylines: Paylines): void {
        super.createPaylines(paylines);

        if (this.paylines != null) {
            this.paths = [];

            for (let i = 0; i < 10; i++) {
                this.paths.push({ parts: [], borders: [] });

                const path = <PIXI.Container>(
                    (<PIXI.Container>(
                        (<PIXI.Container>this.paylines.getChildByName("Payline_" + (i + 1))).getChildByName("Elements")
                    )).getChildByName("Path")
                );

                for (let j = 0; j < 5; j++) {
                    this.paths[i].parts.push(<ImageElement>path.getChildByName("Part" + j));
                    this.paths[i].borders.push(<ImageElement>path.getChildByName("Border" + j));
                }
            }
        }
    }

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (caseType === Payline.CaseWin && this.currentMultiplierIndex >= 3) {
            caseType = Payline.CaseHighWin;
        }

        super.winPaylineGoToCase(winIndex, caseType);

        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                const path = this.paths[win.paylineId - 1];

                for (let i = 0; i < win.mask.length; i++) {
                    path.borders[i].visible = win.mask[i] === 1;
                    path.parts[i].visible = !path.borders[i].visible;
                }
            }
        }
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        super.createAnimations();

        this.counter = this.childs["Counter"];

        this.animationsContainer.addChild(this.counter);
    }

    protected getAnimationIndex(): number {
        return this.currentMultiplierIndex - 3;
    }

    protected startAnimation(): void {
        if (this.teasing != null) {
            this.teasing.goToCase("SHOW");
        }

        super.startAnimation();
    }

    protected stopAnimation(): void {
        if (this.teasing != null) {
            this.teasing.goToCase("FORCE");
        }

        super.stopAnimation();
    }

    protected onAnimationFinished(): void {
        if (this.teasing != null) {
            this.teasing.goToCase("HIDE");
        }

        super.onAnimationFinished();
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    private resetCounter(): void {
        Tweens.removeTweens(this.counter, "all");

        this.counter.alpha = 0;
    }

    private runCounterStep1(expanded: boolean): void {
        let amountFrom = "0";
        let amountTo = "0";

        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }

        if (amountTo === "0") {
            return;
        }

        if (expanded) {
            PlayList.play("SND_BaseCreditboxEnd");

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.counter.start(
                    parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                    parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                    10,
                );
            } else {
                ImageTextCounterElement.Round = false;
                this.counter.start(parseFloat(amountFrom), parseFloat(amountTo), 10);
            }

            Tweens.removeTweens(this.counter, "all");
            Tweens.alphaTween(this.counter, {
                alphas: [this.counter.alpha, 1],
                tweenDuration: 10,
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [
                    { x: 0.85, y: 0.85 } as PIXI.Point,
                    { x: 0.9, y: 0.9 } as PIXI.Point,
                    { x: 0.85, y: 0.85 } as PIXI.Point,
                ],
                tweenDuration: 20,
                tweenEase: "backOut",
            });
        } else {
            if (this.counter.alpha === 0) {
                PlayList.play("SND_BaseWin0" + (this.currentMultiplierIndex + 1));
            }

            const step = (0.85 - 0.5) / this.currentWins.length;
            const tweenDuration = this.currentMultiplierIndex == 0 ? 10 : 25;
            const counterDuration = this.currentMultiplierIndex == 0 ? 0 : 1;

            Tweens.removeTweens(this.counter, "all");
            Tweens.positionTween(this.counter, {
                tweenX: [this.counter.x, this.counter.x],
                tweenY: [this.counter.y, this.counter.y],
                tweenDuration: tweenDuration,
                callbackFunction: (): void => {
                    PlayList.play("SND_BaseCreditboxLoop");

                    if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                        ImageTextCounterElement.Round = true;
                        this.counter.start(
                            parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                            parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                            25 * counterDuration,
                        );
                    } else {
                        ImageTextCounterElement.Round = false;
                        this.counter.start(parseFloat(amountFrom), parseFloat(amountTo), 25 * counterDuration);
                    }
                },
            });
            Tweens.alphaTween(this.counter, {
                alphas: [this.counter.alpha, 1],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [
                    { x: 0.5 + step * this.currentCounterStep, y: 0.5 + step * this.currentCounterStep } as PIXI.Point,
                    {
                        x: 0.5 + step * (this.currentCounterStep + 1),
                        y: 0.5 + step * (this.currentCounterStep + 1),
                    } as PIXI.Point,
                ],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
                tweenEase: "backInOut",
                callbackFunction: (): void => {
                    if (counterDuration == 0) {
                        this.onCalculationFinished();
                    }
                    PlayList.stop("SND_BaseCreditboxLoop");
                    PlayList.play("SND_BaseCreditboxEnd");
                },
            });
        }
    }

    private runCounterStep2(): void {
        const tweenDuration = this.currentMultiplierIndex == 0 ? 1 : 30;

        if (this.currentMultiplierIndex > 0) {
            this.counter.glowAsWin();
        }

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 1],
            tweenDuration: tweenDuration,
            callbackFunction: (): void => {
                PlayList.play("SND_BaseCreditboxEnd");
            },
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [
                { x: 0.85, y: 0.85 } as PIXI.Point,
                { x: 1, y: 1 } as PIXI.Point,
                { x: 0.85, y: 0.85 } as PIXI.Point,
            ],
            tweenDuration: 15,
            tweenDelay: tweenDuration,
        });
    }

    private runCounterStep3(): void {
        PlayList.play("SND_BaseWinAway");

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 0],
            tweenDuration: 30,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [{ x: 0.85, y: 0.85 } as PIXI.Point, { x: 1.85, y: 1.85 } as PIXI.Point],
            tweenDuration: 30,
        });
    }

    private runCounterOutro(expanded: boolean): void {
        PlayList.stop("SND_BaseCreditboxLoop");
        PlayList.stop("SND_BaseCreditboxEnd");
        PlayList.play("SND_BaseWinAway");

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(0, parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(0, this.currentWin, 0);
        }

        if (expanded) {
            Tweens.removeTweens(this.counter, "all");
            Tweens.alphaTween(this.counter, {
                alphas: [1, 0],
                tweenDuration: 5,
                tweenDelay: 70,
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [{ x: 0.85, y: 0.85 } as PIXI.Point, { x: 0.85, y: 0.85 } as PIXI.Point],
                tweenDuration: 5,
                tweenDelay: 70,
            });
        } else {
            const tweenDuration = this.currentMultiplierIndex == 0 ? 10 : 20;

            Tweens.removeTweens(this.counter, "all");
            Tweens.alphaTween(this.counter, {
                alphas: [1, 0],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [{ x: 0.85, y: 0.85 } as PIXI.Point, { x: 1.85, y: 1.85 } as PIXI.Point],
                tweenDuration: tweenDuration,
                tweenDelay: tweenDuration,
            });
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        this.currentIdleSteps = this.getIdleSteps();

        this.idleStep();
    }

    protected getInitialIdleDelay(): number {
        return 120;
    }

    protected getIdleDelay(): number {
        return 60 * (Math.floor(Math.random() * 3) + 2);
    }

    private idleStep(): void {
        const positions = this.currentIdleSteps.shift();

        for (let i = 0; i < positions.length; i++) {
            this.currentSymbols[positions[i][0]][positions[i][1]].goToCase(Symbol.CaseIdle);
        }

        if (this.currentIdleSteps.length === 0) {
            this.startTimer(WinAnimator.Idle, 90 + this.getIdleDelay());
        } else {
            this.startTimer(WinAnimator2.IdleStep, 15);
        }
    }

    private getIdleSteps(): Array<Array<Array<number>>> {
        switch (Math.round(Math.random() * 5)) {
            default:
            case 0: // TL
                return [
                    [[0, 0]],
                    [
                        [0, 1],
                        [1, 0],
                    ],
                    [
                        [0, 2],
                        [1, 1],
                        [2, 0],
                    ],
                    [
                        [1, 2],
                        [2, 1],
                        [3, 0],
                    ],
                    [
                        [2, 2],
                        [3, 1],
                        [4, 0],
                    ],
                    [
                        [3, 2],
                        [4, 1],
                    ],
                    [[4, 2]],
                ];
            case 1: // BR
                return [
                    [[4, 2]],
                    [
                        [3, 2],
                        [4, 1],
                    ],
                    [
                        [2, 2],
                        [3, 1],
                        [4, 0],
                    ],
                    [
                        [1, 2],
                        [2, 1],
                        [3, 0],
                    ],
                    [
                        [0, 2],
                        [1, 1],
                        [2, 0],
                    ],
                    [
                        [0, 1],
                        [1, 0],
                    ],
                    [[0, 0]],
                ];
            case 2: // TR
                return [
                    [[4, 0]],
                    [
                        [4, 1],
                        [3, 0],
                    ],
                    [
                        [4, 2],
                        [3, 1],
                        [2, 0],
                    ],
                    [
                        [3, 2],
                        [2, 1],
                        [1, 0],
                    ],
                    [
                        [2, 2],
                        [1, 1],
                        [0, 0],
                    ],
                    [
                        [1, 2],
                        [0, 1],
                    ],
                    [[0, 2]],
                ];
            case 3: // BL
                return [
                    [[0, 2]],
                    [
                        [1, 2],
                        [0, 1],
                    ],
                    [
                        [2, 2],
                        [1, 1],
                        [0, 0],
                    ],
                    [
                        [3, 2],
                        [2, 1],
                        [1, 0],
                    ],
                    [
                        [4, 2],
                        [3, 1],
                        [2, 0],
                    ],
                    [
                        [4, 1],
                        [3, 0],
                    ],
                    [[4, 0]],
                ];
            case 4: // from center
                return [
                    [[2, 1]],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [2, 0],
                        [2, 2],
                        [3, 0],
                        [3, 1],
                        [3, 2],
                    ],
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [4, 0],
                        [4, 1],
                        [4, 2],
                    ],
                ];
            case 5: // to center
                return [
                    [
                        [0, 0],
                        [0, 1],
                        [0, 2],
                        [4, 0],
                        [4, 1],
                        [4, 2],
                    ],
                    [
                        [1, 0],
                        [1, 1],
                        [1, 2],
                        [2, 0],
                        [2, 2],
                        [3, 0],
                        [3, 1],
                        [3, 2],
                    ],
                    [[2, 1]],
                ];
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimator2.ts
 **/